import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, ShieldCheck, ArrowUpRight, ExternalLink, RefreshCw } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

/**
 * SetupInstagram — one-time, admin-only, hidden setup screen for entering
 * the Meta Instagram App Secret directly into the server-side environment.
 *
 * The secret is submitted via HTTPS to POST /api/instagram/setup-secret,
 * where it is written to /app/backend/.env atomically and the current
 * process env is updated. The value is NEVER logged, echoed, or returned
 * in any subsequent API response.
 *
 * Route: /setup/instagram (not linked from any navigation; direct URL only).
 * Access: Admin JWT required. Non-admin visitors see a sign-in prompt.
 * Lifecycle: once the setup succeeds, the backend writes a sentinel
 * (`.instagram_setup_lock`) and this page auto-hands the owner a single
 * authorization URL to click.
 */
export default function SetupInstagram() {
    const { user, loading } = useAuth();
    const [params] = useSearchParams();
    const igErr = params.get("ig_err") || "";
    const [status, setStatus] = useState(null);
    const [statusLoading, setStatusLoading] = useState(true);
    const [secret, setSecret] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [starting, setStarting] = useState(false);

    async function fetchStatus() {
        setStatusLoading(true);
        try {
            const { data } = await api.get("/instagram/status");
            setStatus(data);
        } catch (e) {
            // 401 → not admin; fall through to gate UI
            setStatus(null);
        } finally {
            setStatusLoading(false);
        }
    }

    useEffect(() => {
        if (user && user.role === "admin") fetchStatus();
    }, [user]);

    const configured = !!status?.meta_app_configured;
    const missing = status?.meta_app_missing || "";
    const needsAppId = missing.includes("META_INSTAGRAM_APP_ID");
    const setupLocked = !!status?.setup_locked;

    const errMessage = useMemo(() => {
        const map = {
            invalid_state:
                "The authorization link expired or was already used. Click Open Meta Authorization to start a fresh secure flow.",
            missing_params:
                "Meta redirected without the required parameters. Click Open Meta Authorization to try again.",
            code_exchange_failed:
                "Meta rejected the authorization code. Confirm the redirect URI in the Meta app matches this preview URL and try again.",
            long_token_failed:
                "Could not exchange the short-lived token for a long-lived one. Meta may be temporarily unavailable — try again.",
            resolve_failed:
                "Could not read the Instagram profile after authorization. Try again in a moment.",
            wrong_account:
                "You signed in with a different Instagram username. Please sign in as @crewznation and try again.",
            not_professional:
                "@crewznation must be a Professional (Business or Creator) account. Convert it in the Instagram app, then retry.",
            denied:
                "Authorization was cancelled or refused. Click Open Meta Authorization to retry.",
            not_configured:
                "Meta app configuration is incomplete. Contact the developer to verify server env.",
        };
        return map[igErr] || (igErr ? `Unexpected error (${igErr}). Please retry.` : "");
    }, [igErr]);

    async function handleSubmit(e) {
        e.preventDefault();
        const value = secret.trim();
        if (!value || value.length < 8) {
            toast.error("Enter the full App Secret from the Meta app");
            return;
        }
        setSubmitting(true);
        try {
            await api.post("/instagram/setup-secret", { app_secret: value });
            // Clear the field IMMEDIATELY so the value never lingers in DOM.
            setSecret("");
            toast.success("Secret configured.");
            await fetchStatus();
        } catch (err) {
            const detail =
                err?.response?.data?.detail ||
                "Failed to save secret. Confirm you're signed in as admin.";
            toast.error(detail);
        } finally {
            setSubmitting(false);
        }
    }

    /** Mint a fresh authorization URL server-side and navigate the browser
     *  to it immediately. The URL is NEVER stored in React state, browser
     *  history, or clipboard — it is used exactly once, at click time. */
    async function startAuthorization() {
        setStarting(true);
        try {
            const { data } = await api.get("/instagram/oauth/start");
            if (data?.authorization_url) {
                window.location.assign(data.authorization_url);
            } else {
                toast.error("Could not build the authorization URL");
                setStarting(false);
            }
        } catch (err) {
            const detail = err?.response?.data?.detail || "Could not start authorization";
            toast.error(detail);
            setStarting(false);
        }
    }

    // ---- gates ----
    if (loading || user === null) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[var(--crewz-bg)] text-zinc-500 font-body">
                <Loader2 size={20} className="animate-spin" />
            </div>
        );
    }

    if (!user || user.role !== "admin") {
        return (
            <div className="min-h-screen bg-[var(--crewz-bg)] text-[var(--crewz-text)]">
                <Navbar />
                <section className="px-6 md:px-12 py-32 max-w-3xl mx-auto text-center">
                    <p className="font-body text-xs tracking-[0.3em] uppercase text-[var(--crewz-gold)] mb-6">
                        Restricted
                    </p>
                    <h1 className="font-display text-5xl md:text-6xl leading-[0.95] mb-6">
                        Admin Access Required.
                    </h1>
                    <p className="text-[var(--crewz-text-muted)] mb-8">
                        Sign in with the CrewZNatioN admin account to complete Instagram setup.
                    </p>
                    <Link
                        to="/login"
                        state={{ from: "/setup/instagram" }}
                        className="inline-flex items-center gap-2 bg-[var(--crewz-gold)] text-[var(--crewz-bg)] hover:bg-[var(--crewz-gold-soft)] px-7 py-4 text-sm tracking-[0.22em] uppercase font-bold transition-colors duration-300"
                        data-testid="setup-ig-signin-cta"
                    >
                        Sign In
                        <ArrowUpRight size={14} />
                    </Link>
                </section>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[var(--crewz-bg)] text-[var(--crewz-text)]" data-testid="setup-instagram-page">
            <Navbar />
            <section className="pt-28 md:pt-36 pb-24 px-6 md:px-12 max-w-3xl mx-auto">
                <p className="overline overline-primary mb-4">Admin · Instagram Setup</p>
                <h1 className="font-display text-4xl md:text-5xl leading-[1.05] mb-6">
                    Connect <strong style={{ fontWeight: 800 }}>@crewznation</strong>
                </h1>
                <p className="text-[17px] leading-[1.7] text-[var(--crewz-text)]/80 mb-10 max-w-2xl">
                    Paste your Instagram App Secret below. It is written directly to the server
                    environment over HTTPS, encrypted at rest by the file-system, and never
                    logged, echoed, or returned in any response. This screen self-locks after
                    the first successful submission.
                </p>

                {statusLoading ? (
                    <div className="flex items-center gap-2 text-zinc-500">
                        <Loader2 size={16} className="animate-spin" />
                        Checking server configuration…
                    </div>
                ) : (
                    <>
                        {/* Server config summary */}
                        <div className="border border-white/10 bg-white/[0.02] p-5 mb-8 text-[13px] leading-[1.7]" data-testid="setup-ig-server-summary">
                            <div className="grid grid-cols-[180px_1fr] gap-y-2">
                                <span className="text-zinc-500">Meta App configured</span>
                                <span className={configured ? "text-emerald-400" : "text-amber-400"}>
                                    {configured ? "Yes" : "No — missing " + (missing || "unknown")}
                                </span>
                                <span className="text-zinc-500">Redirect URI</span>
                                <code className="font-mono text-[12px] break-all">{status?.redirect_uri || "—"}</code>
                                <span className="text-zinc-500">Required scope</span>
                                <span>{(status?.required_scopes || []).join(", ") || "—"}</span>
                                <span className="text-zinc-500">Setup lock</span>
                                <span>{setupLocked ? "Locked (already configured)" : "Unlocked"}</span>
                                <span className="text-zinc-500">Live connection</span>
                                <span>{status?.connection?.connected ? `Connected as @${status.connection.username}` : "Not yet connected"}</span>
                            </div>
                        </div>

                        {needsAppId && (
                            <div className="border border-amber-400/40 bg-amber-400/5 px-5 py-4 mb-6 text-[13px]">
                                The Meta <code>App ID</code> is not set on the server yet. Ask
                                the developer to add <code>META_INSTAGRAM_APP_ID</code> before
                                submitting the secret.
                            </div>
                        )}

                        {/* Callback error banner */}
                        {errMessage && (
                            <div
                                className="border border-amber-400/40 bg-amber-400/5 px-5 py-4 mb-6 text-[13px] leading-[1.7]"
                                data-testid="setup-ig-error-banner"
                            >
                                {errMessage}
                            </div>
                        )}

                        {/* Secret input — hidden if already configured */}
                        {!configured && !needsAppId && (
                            <form onSubmit={handleSubmit} className="space-y-4" data-testid="setup-ig-form" autoComplete="off">
                                <label className="block">
                                    <span className="block text-[12px] uppercase tracking-[0.22em] text-zinc-400 mb-2">
                                        Meta App Secret
                                    </span>
                                    <input
                                        type="password"
                                        value={secret}
                                        onChange={(e) => setSecret(e.target.value)}
                                        placeholder="Paste the value here — it will be masked"
                                        className="w-full bg-transparent border border-white/15 focus:border-[var(--crewz-gold)] outline-none px-4 py-3 font-mono text-[14px] tracking-tight"
                                        autoComplete="off"
                                        autoCorrect="off"
                                        spellCheck={false}
                                        data-testid="setup-ig-secret-input"
                                    />
                                </label>
                                <button
                                    type="submit"
                                    disabled={submitting || !secret.trim()}
                                    className="inline-flex items-center gap-2 bg-[var(--crewz-gold)] text-[var(--crewz-bg)] hover:bg-[var(--crewz-gold-soft)] disabled:opacity-50 disabled:cursor-not-allowed px-6 py-3 text-[13px] tracking-[0.22em] uppercase font-bold transition-colors duration-300"
                                    data-testid="setup-ig-submit"
                                >
                                    {submitting ? <Loader2 size={14} className="animate-spin" /> : <ShieldCheck size={14} />}
                                    {submitting ? "Saving…" : "Save Secret & Continue"}
                                </button>
                                <p className="text-[12px] text-zinc-500">
                                    The value stays server-side. No copy is kept in your browser,
                                    the URL bar, or the API response.
                                </p>
                            </form>
                        )}

                        {/* Post-success: fresh single-click authorization */}
                        {configured && (
                            <div className="space-y-4" data-testid="setup-ig-authorize">
                                {!status?.connection?.connected ? (
                                    <>
                                        <p className="text-[15px] text-emerald-400">
                                            Secret configured. Click below to authorize @crewznation on Meta.
                                        </p>
                                        <button
                                            type="button"
                                            onClick={startAuthorization}
                                            disabled={starting}
                                            className="inline-flex items-center gap-2 bg-[var(--crewz-gold)] text-[var(--crewz-bg)] hover:bg-[var(--crewz-gold-soft)] disabled:opacity-50 px-6 py-3 text-[13px] tracking-[0.22em] uppercase font-bold transition-colors duration-300"
                                            data-testid="setup-ig-open-authorization"
                                        >
                                            {starting ? <Loader2 size={14} className="animate-spin" /> : (errMessage ? <RefreshCw size={14} /> : <ExternalLink size={14} />)}
                                            {starting ? "Preparing…" : (errMessage ? "Retry Authorization" : "Open Meta Authorization")}
                                        </button>
                                        <p className="text-[12px] text-zinc-500">
                                            A fresh single-use authorization link is created the moment
                                            you click this button; it is never reused after a redirect.
                                        </p>
                                    </>
                                ) : (
                                    <div className="border border-emerald-400/40 bg-emerald-400/5 p-5" data-testid="setup-ig-connected">
                                        <p className="text-[15px] text-emerald-400 mb-2">
                                            Connected as @{status.connection.username}
                                        </p>
                                        <p className="text-[12px] text-zinc-500">
                                            /social and /ar/social now show live @crewznation content.
                                        </p>
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </section>
            <Footer />
        </div>
    );
}
